import { onConsentChange } from './consent'
import { loadScript } from './loadScript'
import { trackPageView } from './track'

// Must match the checkout domain used in src/lib/shopify.ts, since gtag's
// cross-domain linker needs to know which domain to stitch click IDs across.
const SHOPIFY_CHECKOUT_DOMAIN =
  process.env.GATSBY_SHOPIFY_STOREFRONT_DOMAIN || 'shop.dogio.fi'

let initialised = false

const sendConsentUpdate = (granted: boolean): void => {
  const value = granted ? 'granted' : 'denied'
  window.gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  })
}

// Loads gtag.js and sends Consent Mode's default ("denied") signal on every
// visit, regardless of the visitor's consent decision — this is the FR-011/
// FR-012 protocol-level signal Google's Consent Mode expects, distinct from
// application-level Tracking Event data (page views/events), which stays
// fully gated behind hasConsent('analytics') in track.ts (research.md §7).
const initGtag = (): void => {
  if (initialised || typeof window === 'undefined') {
    return
  }
  const measurementId = process.env.GATSBY_ANALYTICS_TRACKING_ID
  if (!measurementId) {
    return
  }
  initialised = true

  try {
    window.dataLayer = window.dataLayer || []
    // gtag.js only treats entries that are the native `arguments` object as
    // commands ('js' / 'config' / 'event'). A plain array gets interpreted as a
    // data layer variable push and is silently ignored, which is why the initial
    // `config` never reached Tag Assistant. Forward `arguments` verbatim so the
    // real loader replays every queued command once it finishes loading.
    window.gtag = function gtag() {
      window.dataLayer.push(arguments as unknown as unknown[])
    }

    // Consent Mode requires the 'default' command to be queued before any
    // other command (research.md §4) — denied until the visitor decides.
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })

    window.gtag('js', new Date())
    // send_page_view disabled: GA4's automatic page_view would otherwise
    // fire the instant this config command runs, i.e. on every visit before
    // consent — page views are exclusively track.ts's consent-gated
    // trackPageView() now (research.md §5).
    window.gtag('config', measurementId, { send_page_view: false })

    const conversionId = process.env.GATSBY_ADS_CONVERSION_ID
    if (conversionId) {
      window.gtag('config', conversionId, {
        linker: {
          domains: [SHOPIFY_CHECKOUT_DOMAIN],
        },
      })
    }

    loadScript(
      'gtag-js',
      `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    )
  } catch {
    // A broken GA4 loader must never break page rendering (FR-012).
  }
}

export const registerGtagLoader = (): void => {
  initGtag()

  // Consent Mode's 'default' already declared everything denied, so the
  // baseline here is "not granted" — only a transition away from that is
  // worth signaling (avoids a redundant 'update' echoing 'default').
  let lastAnalyticsGranted = false

  onConsentChange((state) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return
    }
    if (lastAnalyticsGranted === state.analytics) {
      return
    }
    const wasGranted = lastAnalyticsGranted
    lastAnalyticsGranted = state.analytics

    try {
      sendConsentUpdate(state.analytics)
    } catch {
      // A broken consent signal must never break page rendering (FR-012).
    }

    // send_page_view:false means GA4 never auto-captures the current page
    // on its own — replay it explicitly the moment analytics consent is
    // (re-)granted, so the landing page isn't silently missed.
    if (state.analytics && !wasGranted) {
      trackPageView(window.location.pathname)
    }
  })
}
